import {
  Button,
  Card,
  Title,
  Text,
  Anchor,
  Skeleton,
  Modal,
  Center,
  Loader,
  Stack,
  Textarea,
  TextInput,
  Divider,
  Flex,
  Tooltip,
} from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import ReactGA from 'react-ga4';
import { useMediaQuery } from '@mantine/hooks';
import { AgendaConfiguration } from '@/api/agendaConfiguration';
import { Booking } from '@/api/booking';

import { useEloApi, ApiError } from '@/api/api';
import { useError } from '@/contexts/ErrorContext';
import { MyDayPicker } from '@/components/MyDayPicker/MyDayPicker';
import { GoogleCalendarEvent, User } from '@/api/users';

import classes from './editBooking.module.css';
import { DeleteModal } from '@/components/Modals/Modals';
import { PhoneInput } from '@/components/PhoneInput/PhoneInput';
import { useLocation, useParams } from 'react-router-dom';
import { IconChecks, IconCircle, IconCircleCheck } from '@tabler/icons-react';
import toast from 'react-hot-toast';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

interface SaveBookingPageProps {
  localUser: User;
  extension: string;
  editBooking: Booking;
}

interface TableRowProps {
  label: string;
  loading: boolean;
  value?: string;
  onChange?: (value: string) => void;
  isValid?: (value: string) => boolean;
  errorMessage?: string;
  isAnchor?: boolean;
  onClick?: () => void;
}
const TableRow = ({
  label,
  loading,
  value,
  onChange,
  isValid,
  isAnchor = false,
  onClick,
}: TableRowProps) => {
  const [localValue, setLocalValue] = useState(value);
  useEffect(() => {
    if (isValid && onChange && typeof localValue === typeof 'r') {
      if (isValid(localValue as string)) {
        setHasError(false);
        onChange(localValue as string);
      } else {
        setHasError(true);
      }
    }
  }, [localValue]);

  const [hasError, setHasError] = useState(false);

  return (
    <Skeleton visible={loading}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          // gridTemplateColumns: '105px 1fr',
          padding: '5px 0px',
          marginBottom: '1px',
          alignItems: 'flex-start',
        }}
        className={`${classes.font} ${onClick ? classes.onHoverTableRow : ''}}`}
        onClick={onClick}
      >
        <Text
          style={{
            fontWeight: 'bold',
          }}
          className={classes.font}
        >
          {label}
        </Text>
        {value && onChange ? (
          <input
            value={localValue}
            onChange={(e) => {
              setLocalValue(e.target.value);
            }}
            style={{
              all: 'unset',
              outline: hasError ? '1px solid red' : 'none',
              width: '100%',
            }}
            className={classes.font}
          />
        ) : isAnchor ? (
          <Anchor
            href={value}
            target="_blank"
            className={classes.font}
            style={{
              width: '100%',
              wordBreak: 'break-all',
              textOverflow: 'ellipsis',
              textWrap: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {value}
          </Anchor>
        ) : (
          <Text className={classes.font} style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
            {value}
          </Text>
        )}
      </div>
    </Skeleton>
  );
};

function convertToAmPm(time: string) {
  const [hour, minute, seconds] = time.split(':').map(Number);
  const period = hour >= 12 ? 'pm' : 'am';
  const hour12 = hour % 12 || 12;
  const formattedTime = `${hour12}:${minute.toString().padStart(2, '0')}${period}`;

  return formattedTime;
}

function convertTo24Hour(time: string) {
  const period = time.slice(-2).toLowerCase(); // 'am' o 'pm'
  const [hour, minute] = time.slice(0, -2).split(':').map(Number);

  const hour24 = period === 'pm' ? (hour % 12) + 12 : hour % 12; // Convierte a 24 horas
  const formattedTime = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;

  return formattedTime;
}

function isValidEmail(email: string): boolean {
  if (email === '') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function isValidPhoneNumber(phone: string): boolean {
  if (phone === '') return false;
  const phoneRegex = /^\+?(\d[\s-]?){7,25}$/;
  return phoneRegex.test(phone.trim());
}

export const EditBooking = ({
  extension,
  editBooking,
  localUser,
}: SaveBookingPageProps) => {
  const location = useLocation();
  const isConfirmPath = location.pathname.endsWith('/confirm');
  const [isConfirmed, setIsConfirmed] = useState("");

  const hasSentPageView = useRef(false);
  useEffect(() => {
    if (!hasSentPageView.current) {
      ReactGA.send({
        hitType: 'pageview',
        page: '/save-booking',
      });
      hasSentPageView.current = true;
    }
  }, []);

  const { t } = useTranslation();

  const [booking, setBooking] = useState<Booking>({ ...editBooking });
  const [loading, setLoading] = useState(false);

  const api = useEloApi({});
  const errorContext = useError();

  const [isReScheduling, setIsReScheduling] = useState(false);

  const [commentModal, setCommentModal] = useState({
    modal: false,
    for: 'delete',
    startDate: new Date(),
  });
  const [comments, setComments] = useState('');

  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const updateBooking = async (fields: Partial<Booking>) => {
    setLoading(true);
    try {
      if (fields?.name) if (fields.name === '' || fields.name === undefined) return;
      if (fields?.phone) {
        if (
          fields.phone === '' ||
          fields.phone === undefined ||
          isValidPhoneNumber(fields.phone) == false
        )
          return;
      }
      if (fields?.email) {
        if (
          fields.email === '' ||
          fields.email === undefined ||
          isValidEmail(fields.email) == false
        )
          return;
      }

      const token = await recaptchaRef.current?.executeAsync();
      if (!token) return;

      let hasTimezone;
      if (fields?.startDate) {
        hasTimezone = !!editBooking.timeZone;
      }

      const res = await api.updateBooking(
        {
          id: editBooking.id,
          ...fields,
          hasTimezone,
        },
        extension,
        token
      );
      setBooking(res);
    } catch (error) {
      let errorMessage;
      if (error instanceof ApiError) {
        errorMessage = error.message;
      }
      errorContext.setError(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
      setIsReScheduling(false);
    }
  };

  const deleteBooking = async (comments?: string) => {
    if (!editBooking) return;
    setLoading(true);
    try {
      await api.deleteBooking(editBooking.id, undefined, extension, comments);
      setIsDeleted(true);
    } catch (error) {
      let errorMessage;
      if (error instanceof ApiError) {
        errorMessage = error.message;
      }
      errorContext.setError(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
      setIsDeleting(false);
    }
  };

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    editBooking?.startDate ? new Date(editBooking?.startDate) : undefined
  );
  const prevSelectedDate = useRef(selectedDate);

  const [selectedHour, setSelectedHour] = useState<string | undefined>(
    editBooking?.startDate
      ? convertToAmPm(new Date(editBooking.startDate).toLocaleTimeString('es-MX'))
      : undefined
  );
  const prevSelectedHour = useRef(selectedHour);

  const [forceAvailable, setForceAvailable] = useState(
    selectedDate && selectedDate.getDate() && selectedHour
      ? { [selectedDate.getDate()]: [selectedHour] }
      : {}
  );

  const [agendaConfiguration, setAgendaConfiguration] = useState<AgendaConfiguration | undefined>();
  const getConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAgendaConfiguration(editBooking.agendaConfigurationId, extension);
      setAgendaConfiguration(res);
    } catch(e) {
      console.error(e);
      if (e instanceof ApiError) {
        errorContext.setError(e.message);
      } else {
        throw e;
      }
    } finally {
      setLoading(false);
    }
  }, [editBooking.agendaConfigurationId, extension]);
  useEffect(() => {getConfig()}, []);

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [googleCalendarEvents, setGoogleCalendarEvents] = useState<GoogleCalendarEvent[]>([]);

  const [dayPickerHeight, setDayPickerHeight] = useState<number | undefined>(undefined);
  const [dayPickerWidth, setDayPickerWidth] = useState<number | undefined>(undefined);
  const dayPickerRef = useRef<HTMLDivElement>(null);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [loadingConfirmation, setLoadingConfirmation] = useState(false);
  const confirmBooking = useCallback(async () => {
    if (!isConfirmPath) return;
    setLoadingConfirmation(true);
    setIsConfirmed("");
    try {
      const res = await api.confirmAssistance(
        extension,
        editBooking.bookingUrl
      );
      setBooking({ ...booking, status: 0 });
      setIsConfirmed(res.message);
    } catch (e) {
      console.error(e);
      if (e instanceof ApiError) {
        toast.error('Error al confirmar', { duration: 5000 });
      } else {
        throw e;
      }
    } finally {
      setLoadingConfirmation(false);
    }
  }, [isConfirmPath, editBooking.bookingUrl, editBooking.status, extension]);
  useEffect(() => {
    if (isConfirmPath) {
      confirmBooking();
    }
  }, [isConfirmPath, booking.status]);

  useEffect(() => {
    if (dayPickerRef.current) {
      setDayPickerHeight(dayPickerRef.current.offsetHeight);
      setDayPickerWidth(dayPickerRef.current.offsetWidth);
    }
    if (!selectedDate) {
      setSelectedHour(undefined);
    }
    if (selectedDate && selectedDate !== prevSelectedDate.current) {
      setSelectedHour(undefined);

      prevSelectedDate.current = selectedDate;
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedHour && selectedHour !== prevSelectedHour.current) {
      prevSelectedHour.current = selectedHour;
    }
  }, [selectedHour]);

  useEffect(() => {
    if (selectedHour && selectedDate && !isReScheduling) {
      setForceAvailable({ [selectedDate.getDate()]: [selectedHour] });
    }
  }, [selectedDate, selectedHour, isReScheduling]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        minHeight: '100dvh',
        backgroundColor: 'var(--mantine-background)',
      }}
    >
      <style>
        {`
        .grecaptcha-badge { visibility: hidden; }
        `}
      </style>
      <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} size="invisible" />
      <Card shadow="sm" padding="lg" radius="md" withBorder className={classes.card}>
        <Flex
          style={{
            marginBottom: '15px',
          }}
          justify={"space-between"} align={"center"}
        >
          <Title order={3} display={"inline"}>{`${t('edit_booking.session_with')}${booking?.name}`}</Title>
          <Flex gap={"sm"}>
            {
              loadingConfirmation
                ? <Text className='loading-dots'>Esperando respuesta</Text>
                : booking?.status === 0
                  ? <Text>Sesión Confirmada</Text>
                  : <Text>Sesión sin confirmar</Text>
            }
            {loadingConfirmation ? (
              <Loader size={'sm'}></Loader>
            ) : (
              <IconChecks
                size={24}
                color={
                  booking.status === 0
                    ? 'var(--mantine-color-blue-filled)'
                    : 'var(--mantine-color-gray-5)'
                }
              />
            )}
          </Flex>
        </Flex>
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'flex-start',
            gap: '12px',
            marginLeft: '12px',
            marginRight: '12px',
            marginBottom: '15px',
          }}
        >
          <Button
            variant="outline"
            size="xs"
            onClick={() =>
              setCommentModal({
                ...commentModal,
                modal: true,
                for: 'delete',
              })
            }
          >
            {t('edit_booking.cancel_session')}
          </Button>
          <Button size="xs" onClick={() => setIsReScheduling(true)}>
            {t('edit_booking.reschedule')}
          </Button>
        </div>

        <Divider my="md" mx={12} size="md" />
        <div
          style={{
            padding: '0px 12px',
          }}
        >
          <Title order={3} style={{ marginBottom: '8px', color: '#228BE6' }}>
            {t('edit_booking.details')}
          </Title>

          <TableRow
            loading={loading}
            label={t('edit_booking.datetime')}
            value={`${selectedDate?.toLocaleDateString('es-MX')} - ${selectedHour}`}
            onClick={() => setIsReScheduling(true)}
          />

          <Flex
            wrap="wrap"
            gap="sm"
            direction={isMobile ? 'column' : 'row'}
            justify="space-between"
          >
            <TextInput
              label={t('edit_booking.name')}
              value={booking?.name || ''}
              onChange={(event) => setBooking({ ...booking, name: event.currentTarget.value })}
              disabled={loading}
              error={!booking?.name && 'El nombre no puede estar vacío'}
              style={{ width: isMobile ? '100%' : '45%' }}
            />

            <TextInput
              label={t('edit_booking.email')}
              value={booking?.email || ''}
              onChange={(event) => setBooking({ ...booking, email: event.currentTarget.value })}
              disabled={loading}
              error={!isValidEmail(booking?.email) && 'Correo inválido'}
              style={{ width: isMobile ? '100%' : '45%' }}
            />
            <PhoneInput
              label={t('edit_booking.phone')}
              defaultValue={booking?.phone || ''}
              defaultCallingCode={localUser?.defaultCountryCode}
              onChange={(value, countryCode, isValid) => {
                setBooking({
                  ...booking,
                  phone: value,
                });
                setBooking({ ...booking, phone: value });
              }}
              disabled={loading}
              style={{ width: isMobile ? '100%' : '45%', margin: '10px 0px' }}
            />
          </Flex>

          <TableRow
            loading={loading}
            label={
              booking?.locationDetails ? t('edit_booking.location') : t('edit_booking.meeting_url')
            }
            value={booking?.locationDetails ? booking?.locationDetails : booking?.meetingUrl}
            isAnchor={!booking?.locationDetails}
          />

          {booking?.stripePaymentLink && (
            <TableRow
              loading={loading}
              label={t('edit_booking.payment_link')}
              value={booking?.stripePaymentLink}
              isAnchor={true}
            />
          )}
        </div>
        <div
          style={{
            marginTop: '16px',
            marginBottom: '8px',
            display: 'flex',
          }}
        >
          <Button
            fullWidth={isMobile}
            style={{
              margin: 'auto',
            }}
            variant="outline"
            onClick={() => {
              updateBooking({
                name: booking.name,
                phone: booking.phone,
                email: booking.email,
              });
            }}
          >
            {t('edit_booking.save_changes')}
          </Button>
        </div>
      </Card>
      <Modal withCloseButton={false} opened={isConfirmPath && !!isConfirmed} onClose={() => setIsConfirmed("")}>
        <Stack justify="center" align="center">
          <IconCircleCheck size={128} color="var(--mantine-color-green-7)" />
          <Title order={2}>{t('edit_booking.booking_confirmed')}</Title>
          <Text>{isConfirmed}</Text>
        </Stack>
      </Modal>
      <DeleteModal
        opened={isDeleting}
        onClose={() => setIsDeleting(false)}
        onSuccess={() => deleteBooking(comments)}
        modalMessage={{
          title: t('edit_booking.cancel_session'),
          no: t('edit_booking.modal.no'),
          yes: t('edit_booking.modal.yes'),
        }}
      />
      <Modal
        opened={commentModal.modal}
        onClose={() => {
          setCommentModal({ ...commentModal, modal: false });
          setComments('');
        }}
        title={t(`edit_booking.comment_modal.${commentModal.for}_title`)}
      >
        <Textarea
          label={t(`edit_booking.comment_modal.${commentModal.for}_label`)}
          autosize
          minRows={5}
          maxRows={10}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          disabled={loading}
          style={{
            padding: '0 4px',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', marginTop: '16px' }}>
          <Button
            variant="outline"
            size="sm"
            style={{
              fontSize: '0.75rem',
              padding: '8px 12px',
            }}
            disabled={loading}
            onClick={() => {
              if (commentModal.for === 'update') {
                setSelectedDate(
                  editBooking?.startDate ? new Date(editBooking?.startDate) : undefined
                );
                setTimeout(() => {
                  setSelectedHour(
                    editBooking?.startDate
                      ? convertToAmPm(new Date(editBooking.startDate).toLocaleTimeString('es-MX'))
                      : undefined
                  );
                }, 1);
              }
              setCommentModal({ ...commentModal, modal: false });
              setComments('');
            }}
          >
            {t(`edit_booking.comment_modal.${commentModal.for}_no`)}
          </Button>
          <Button
            size="sm"
            style={{
              fontSize: '0.75rem',
              padding: '8px 12px',
            }}
            disabled={loading}
            onClick={async () => {
              if (commentModal.for === 'delete') {
                setCommentModal({ ...commentModal, modal: false });
                setIsDeleting(true);
              }
              if (commentModal.for === 'update') {
                await updateBooking({ comments, startDate: commentModal.startDate });
                setCommentModal({ ...commentModal, modal: false });
                setComments('');
              }
            }}
          >
            {t(`edit_booking.comment_modal.${commentModal.for}_yes`)}
          </Button>
        </div>
      </Modal>
      <Modal opened={isDeleted} onClose={() => {}} withCloseButton={false}>
        <>
          <Stack>
            <Title order={3} style={{ textAlign: 'center' }}>
              {t('edit_booking.modal.session_cancelled')}
            </Title>
            <Text style={{ textAlign: 'center' }}>{t('edit_booking.modal.not_accessible')}</Text>
          </Stack>
        </>
      </Modal>
      <Modal
        opened={isReScheduling}
        onClose={() => {
          setSelectedDate(editBooking?.startDate ? new Date(editBooking?.startDate) : undefined);
          setTimeout(() => {
            setSelectedHour(
              editBooking?.startDate
                ? convertToAmPm(new Date(editBooking.startDate).toLocaleTimeString('es-MX'))
                : undefined
            );
          }, 1);
          setIsReScheduling(false);
        }}
        title={t('edit_booking.modal.title')}
        size="auto"
      >
        <div className={classes.dayPickerContainer} ref={dayPickerRef}>
          {loading ? (
            <Center>
              <Loader></Loader>
            </Center>
          ) : (
            agendaConfiguration && (
              <div style={{position: "relative"}}>
                <MyDayPicker
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  agendaConfiguration={agendaConfiguration}
                  bookings={bookings}
                  setBookings={setBookings}
                  extension={extension}
                  googleCalendarEvents={googleCalendarEvents}
                  setGoogleCalendarEvents={setGoogleCalendarEvents}
                  selectedHour={selectedHour}
                  setSelectedHour={setSelectedHour}
                  dayPickerHeight={
                    dayPickerWidth && dayPickerHeight && dayPickerWidth > dayPickerHeight
                      ? dayPickerHeight
                      : undefined
                  }
                  dayPickerWidth={
                    dayPickerWidth && dayPickerHeight && dayPickerWidth < dayPickerHeight
                      ? dayPickerWidth
                      : undefined
                  }
                  forceAvailable={forceAvailable}
                  startMonth={selectedDate}
                  localUser={localUser}
                />
              </div>
            )
          )}
        </div>
        <Skeleton visible={loading}>
          <p style={{ textAlign: 'center' }}>
            <Text style={{ fontWeight: 'bold' }}>
              {selectedDate?.toLocaleDateString('es-MX')} - {selectedHour}
            </Text>
          </p>
        </Skeleton>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '32px' }}>
          <Button
            variant="outline"
            size="md"
            disabled={loading}
            onClick={() => {
              setSelectedDate(
                editBooking?.startDate ? new Date(editBooking?.startDate) : undefined
              );
              setTimeout(() => {
                setSelectedHour(
                  editBooking?.startDate
                    ? convertToAmPm(new Date(editBooking.startDate).toLocaleTimeString('es-MX'))
                    : undefined
                );
              }, 1);
              setIsReScheduling(false);
            }}
          >
            {t('edit_booking.modal.cancel')}
          </Button>
          <Button
            size="md"
            disabled={loading}
            onClick={() => {
              if (!selectedDate || !selectedHour) return;
              const [startHours, startMinutes, seconds] = convertTo24Hour(selectedHour!)
                .split(':')
                .map(Number);
              setIsReScheduling(false);
              setCommentModal({
                modal: true,
                for: 'update',
                startDate: new Date(
                  new Date(selectedDate).getFullYear(),
                  new Date(selectedDate).getMonth(),
                  new Date(selectedDate).getDate(),
                  startHours,
                  startMinutes,
                  0
                ),
              });
            }}
          >
            {t('edit_booking.modal.save')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
